import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, X, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { usePolling } from "../lib/usePolling";
import { devLog } from "../lib/devLog";
import { playChime, isMuted, setMuted } from "../lib/notificationSound";

/**
 * Global on-screen notifier for a single role.
 *
 * Polls `/me/workload` and shows a toast every time a NEW task appears in
 * `targetStatus`. The first tick after login seeds the "seen" set so the
 * user isn't flooded with toasts for tasks that were already waiting.
 *
 * Each toast can be dismissed with the ✕; clicking the body navigates to
 * the project's board.
 */
export function RoleTaskNotifications({ role, targetStatus, accent, label }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);
  const [muted, setMutedState] = useState(() => isMuted());
  const seenRef = useRef(null); // null = uninitialized, Set = seen task IDs

  const isTargetRole = user?.role === role;

  const check = useCallback(async () => {
    if (!isTargetRole) return;
    try {
      const { data } = await api.get("/me/workload");
      const matches = (data?.tasks || []).filter((t) => t.status === targetStatus);
      const currentIds = new Set(matches.map((t) => t.id));

      // First tick after login: seed and skip alerts
      if (seenRef.current === null) {
        seenRef.current = currentIds;
        return;
      }

      const newTasks = matches.filter((t) => !seenRef.current.has(t.id));
      seenRef.current = currentIds;
      if (newTasks.length === 0) return;

      // Subtle chime — respects the persisted mute flag
      playChime();

      setToasts((prev) => {
        const active = new Set(prev.map((t) => t.id));
        const additions = newTasks
          .filter((t) => !active.has(t.id))
          .map((t) => ({ id: t.id, title: t.title, project_id: t.project_id }));
        return [...prev, ...additions];
      });
    } catch (e) {
      devLog.error(`[RoleTaskNotifications:${role}] poll failed`, e);
    }
  }, [isTargetRole, role, targetStatus]);

  // Reset + first fetch on role/status change
  useEffect(() => {
    seenRef.current = null;
    if (isTargetRole) check();
  }, [isTargetRole, check]);

  // Keep polling every 8s even when the tab is hidden — the chime is the
  // primary way a background tab notifies its owner.
  usePolling(check, 8000, isTargetRole, true);

  const dismiss = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const openTask = (t) => {
    dismiss(t.id);
    navigate(`/projects/${t.project_id}`);
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
  };

  if (!isTargetRole || toasts.length === 0) return null;

  const border = `${accent}73`; // ~45% alpha
  const glow = `${accent}26`;   // ~15% alpha
  const iconBg = `${accent}1F`; // ~12% alpha

  return (
    <div
      data-testid={`notifications-${role}`}
      className="fixed z-[100] top-6 right-6 flex flex-col gap-3 max-w-sm"
    >
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={toggleMute}
          data-testid={`notifications-${role}-mute`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-heading font-bold uppercase tracking-wider transition"
          style={{
            background: "rgba(14,14,16,0.85)",
            borderColor: "var(--border)",
            color: muted ? "#94A3B8" : accent,
          }}
          title={muted ? "Unmute chime" : "Mute chime"}
        >
          {muted ? <SpeakerSlash size={12} weight="bold" /> : <SpeakerHigh size={12} weight="bold" />}
          {muted ? "Chime off" : "Chime on"}
        </button>
      </div>
      {toasts.map((t) => (
        <div
          key={t.id}
          data-testid={`toast-${role}-${t.id}`}
          className="pointer-events-auto rounded-md border overflow-hidden shadow-lg"
          style={{
            background: "linear-gradient(180deg, #0E0E10 0%, #14141A 100%)",
            borderColor: border,
            boxShadow: `0 12px 32px ${glow}`,
          }}
        >
          <div className="flex items-start gap-3 p-3">
            <div
              className="mt-0.5 shrink-0 rounded-full p-1.5"
              style={{ background: iconBg, color: accent }}
            >
              <Megaphone size={16} weight="bold" />
            </div>
            <button
              onClick={() => openTask(t)}
              data-testid={`toast-${role}-open-${t.id}`}
              className="flex-1 text-left min-w-0"
            >
              <p
                className="text-[10px] font-mono uppercase tracking-widest mb-1"
                style={{ color: accent }}
              >
                {label}
              </p>
              <p className="text-sm font-heading font-semibold text-white truncate">
                {t.title}
              </p>
              <p className="text-[11px] text-[color:var(--text-tertiary)] mt-1">
                Tap to open →
              </p>
            </button>
            <button
              onClick={() => dismiss(t.id)}
              data-testid={`toast-${role}-dismiss-${t.id}`}
              className="shrink-0 text-[color:var(--text-tertiary)] hover:text-white transition"
              aria-label="Dismiss notification"
              title="Dismiss"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
